import React, { useCallback, useEffect, useState } from 'react';
import { isEmpty, map, size, values } from 'lodash-es';
import { Pagination } from 'antd';
import { CatalogCompany, SupplierSortKey } from '@/shared/types/catalog';
import { useQueryParams } from '@/shared/hooks';
import { ParamKey } from '@/shared/types/url-params';
import { scrollTo } from '@/shared/utils/utils';
import { SUPPLIERS_PAGE_SIZE, SUPPLIER_SORT_OPTIONS } from '@/shared/constants/sourcing-marketplace';
import NoResult from '../NoResult';
import { CategoryConfig, VerticalName } from '@/shared/types/category';
import MarketplaceSortTabs from '../MarketplaceSortTabs';
import SupplierCard from '../SupplierCard';
import Spinner from '@/components/Common/Spinner';
import { supplierRecommendNextItems } from '@/lib/service/catalog';
import ExplorePietraServicesBanner from '../../Common/ExplorePietraServicesBanner';
import { getCreatorHubSourcingHomePath } from '@/shared/utils/sourcing-marketplace-util';
import './index.less';

const SUPPLIER_LIST_CONTAINER_ID = 'SUPPLIER_LIST_CONTAINER_ID';

interface IProps {
  isMobile: boolean;
  vertical: VerticalName;
  suppliers: CatalogCompany[];
  category: CategoryConfig;
  page: number;
  total: number;
  recombeeId: string;
  useRecommendationBackend: boolean;
  recommendedSupplierIds: number[];
}

const BrowseSupplierList = ({
  isMobile,
  vertical,
  suppliers,
  category,
  page,
  total,
  recombeeId,
  useRecommendationBackend,
  recommendedSupplierIds,
}: IProps) => {
  const [displayedSuppliers, setDisplayedSuppliers] = useState<CatalogCompany[]>([]);
  const [nextRecombeeId, setNextRecombeeId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [noMoreToLoad, setNoMoreToLoad] = useState<boolean>(false);

  const { queryParams, setQueryParams } = useQueryParams<{}>();

  useEffect(() => {
    setDisplayedSuppliers(suppliers);
    setNoMoreToLoad(size(suppliers) < SUPPLIERS_PAGE_SIZE);
  }, [suppliers]);

  useEffect(() => {
    if (useRecommendationBackend) {
      setNextRecombeeId(recombeeId);
    }
  }, [recombeeId, useRecommendationBackend]);

  const onUpdatePage = (newPage: number) => {
    scrollTo(SUPPLIER_LIST_CONTAINER_ID);
    setQueryParams({ [ParamKey.PAGE]: newPage });
  };

  const onUpdateSort = (newSort: SupplierSortKey) => {
    scrollTo(SUPPLIER_LIST_CONTAINER_ID);
    setQueryParams({ [ParamKey.SORT]: newSort });
  };

  const renderNoResultContainer = () => {
    return (
      <div className="no-results-container">
        <NoResult vertical={vertical} isMobile={isMobile} />
      </div>
    );
  };

  const fetchMoreRecommendedSuppliers = useCallback(async () => {
    setLoading(true);
    const { list: moreRecombeeSupplierList, recommId: nextRecommId } = await supplierRecommendNextItems({
      recommId: nextRecombeeId,
      size: SUPPLIERS_PAGE_SIZE,
    });
    setDisplayedSuppliers((v) => v.concat(moreRecombeeSupplierList));
    setNextRecombeeId(nextRecommId);
    setLoading(false);
    setNoMoreToLoad(size(moreRecombeeSupplierList) < SUPPLIERS_PAGE_SIZE);
  }, [nextRecombeeId]);

  const renderShowMoreButton = noMoreToLoad ? null : loading ? (
    <Spinner />
  ) : (
    <span className="show-more-button" onClick={fetchMoreRecommendedSuppliers}>
      Show more suppliers
    </span>
  );

  return (
    <div id={SUPPLIER_LIST_CONTAINER_ID} className="browse-supplier-list">
      {isEmpty(suppliers) ? (
        renderNoResultContainer()
      ) : (
        <>
          <div className="sort-tabs-container">
            <MarketplaceSortTabs
              sorts={queryParams.get(ParamKey.SORT) ?? SupplierSortKey.FEATURED}
              sortOptions={values(SUPPLIER_SORT_OPTIONS)}
              applySort={(value: SupplierSortKey) => onUpdateSort(value)}
            />
            {total > 0 && (
              <span className="num-results">
                {total} result{total === 1 ? '' : 's'}
              </span>
            )}
          </div>
          {map(displayedSuppliers, (supplier: CatalogCompany, i: number) => (
            <>
              <SupplierCard
                key={supplier.id}
                supplier={supplier}
                category={category}
                isMobile={isMobile}
                isRecommendedSupplier={recommendedSupplierIds.includes(supplier.id)}
              />
              {i === 2 && (
                <ExplorePietraServicesBanner
                  signupRedirect={getCreatorHubSourcingHomePath()}
                  key={i}
                  utmMedium="browse_suppliers"
                />
              )}
            </>
          ))}
          {useRecommendationBackend ? (
            renderShowMoreButton
          ) : (
            <Pagination
              showSizeChanger={false}
              current={page + 1}
              pageSize={SUPPLIERS_PAGE_SIZE}
              className="pagination"
              total={total}
              showLessItems={true}
              onChange={(nextPage) => {
                onUpdatePage(nextPage - 1);
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default BrowseSupplierList;
